import { ForwardedRef, forwardRef, RefObject, useEffect, useRef } from 'react';

import Focus from '@tiptap/extension-focus';
import styled from 'styled-components';

import { useAiInputAssistModal } from '@components/ai-input-assist/ai-input-modal';
import { AiInputProps } from '@components/ai-input-assist/types';
import { IconButton } from '@components/icon-button';
import { IconAddSmall } from '@components/icons/add-small';
import { IconAI } from '@components/icons/ai';
import { IconObject } from '@components/icons/object';
import { hideModal } from '@components/modal';
import {
  hideExtendedSidebar,
  showExtendedSidebar,
  useExtendedSidebarData,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { classNames } from '@utils/collections';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  FieldMappingData,
  FieldMappingEditorValue,
  FieldMappingNode,
  FieldMappingVariables,
  FieldType,
  transformInput,
  transformJsonOutput,
  transformOutput,
} from './addons/field-mapping';
import { FieldMappingSidebar, FieldMappingSidebarProps } from './field-mapping-sidebar';
import {
  FIELD_MAPPING_REST_MIN_HEIGHT_PX,
  FieldMappingContext,
  FieldMappingContextValue,
  PILL_HEIGHT_PX,
  PILL_ROW_GAP_PX,
  pillRowAlignment,
} from './helpers';
import { BaseTextEditorProps, EditorMethods, TextEditor } from './text-editor';

export type BaseFieldMappingTextEditorProps = BaseTextEditorProps<{
  variables?: FieldMappingVariables;
  actions?: JSX.Element;
}> &
  FieldMappingData & {
    aiOptions?: AiInputProps;
    fieldType?: FieldType;
    hideManualInput?: boolean;
    /**
     * Whether date pills grow the format trigger. Defaults from fieldType: on for text
     * targets (date → text needs a display format), off when the target consumes the raw
     * value (e.g. a date picker).
     */
    hasFormatting?: boolean;
  };

export type FieldMappingTextEditorProps = BaseFieldMappingTextEditorProps & {
  defaultValue?: FieldMappingEditorValue;
  onFormat?: () => void;
  shouldShowFieldMappingOnFocus?: boolean;
  onManualSelect?: (value: any) => void;
  manualData?: any;
  shouldHideMenu?: boolean;
  customShowFieldMappingSidebar?: (props?: Pick<FieldMappingSidebarProps, 'variable'>) => void;
  isEditMode?: boolean;
  disableExpandable?: boolean;
  formLinkType?: FieldMappingContextValue['formLinkType'];
};

const getActionsClassName = (editorUuid: string) => {
  return `field-mapping-actions-${editorUuid}`;
};

export const FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME = 'field-mapping-text-editor';

export const FieldMappingTextEditor = forwardRef(
  (
    {
      defaultValue,
      onChange,
      variableGroups,
      extensions,
      actions,
      aiOptions,
      onFormat,
      fieldMappingId,
      fieldType = 'text',
      hasFormatting,
      hideManualInput = false,
      shouldHideMenu = false,
      shouldShowFieldMappingOnFocus = false,
      onManualSelect,
      manualData,
      className,
      customShowFieldMappingSidebar,
      isEditMode = true,
      disableExpandable = false,
      formLinkType,
      ...rest
    }: FieldMappingTextEditorProps,
    ref: ForwardedRef<EditorMethods>,
  ) => {
    const { t } = useScopedTranslate(locale);
    const editorUuid = useRef(uuid()).current;
    const { isExtended, extendedId } = useExtendedSidebarData();
    const editorRef = useRef<EditorMethods>(null);
    const finalRef = (ref ?? editorRef) as RefObject<EditorMethods>;
    const showAiInputAssistModal = useAiInputAssistModal();
    const parsedDefaultValue = defaultValue
      ? transformInput({
          ...defaultValue,
          variableGroups,
          shouldParseSingleBraceVariables: fieldType !== 'json',
        })
      : undefined;
    const handleChange = (value: string) => {
      onChange?.(fieldType === 'json' ? transformJsonOutput(value) : transformOutput(value));
    };

    const defaultShowFieldMappingSidebar = (
      props: Pick<FieldMappingSidebarProps, 'variable'> = {},
    ) => {
      showExtendedSidebar({
        component: FieldMappingSidebar,
        props: {
          editorRef: finalRef,
          variableGroups: variableGroups ?? [],
          fieldType,
          hideManualInput,
          label: rest.label,
          fieldMappingId: fieldMappingId ?? '',
          onManualSelect,
          manualData,
          shouldHideMenu,
          ...props,
        },
        extendedId: editorUuid,
      });
    };
    // Consumers that need to enrich the sidebar props (e.g. multi-select with
    // `onManualMultiSelect` + `selectedValues`) provide their own opener;
    // everything that opens the sidebar — `+` button, focus, expand pill —
    // goes through this so the custom variant is authoritative.
    const showFieldMappingSidebar = customShowFieldMappingSidebar ?? defaultShowFieldMappingSidebar;
    const actionsId = getActionsClassName(editorUuid);
    const isThisOneExtended = isExtended && extendedId === editorUuid;

    // Group count alone misses fields loading into an already-present group.
    const variableGroupsSignature = variableGroups
      ?.map((group) => `${group.id}:${group.fields.length}`)
      .join('|');
    useEffect(() => {
      if (isThisOneExtended) {
        showFieldMappingSidebar();
      }
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [variableGroupsSignature]);

    const Actions = actions ?? (
      <Stack orientation="horizontal" space={2} id={actionsId}>
        {onFormat && (
          <IconButton
            icon={<IconObject />}
            variant="secondary-purple"
            size="sm"
            title={t('format')}
            dataTestid="field-mapping-format"
            onClick={onFormat}
          />
        )}
        {aiOptions && (
          <IconButton
            icon={<IconAI size={16} />}
            size="sm"
            variant="aiGradient-white"
            onClick={() =>
              showAiInputAssistModal({
                ...aiOptions,
                plaintext: rest.plaintext,
                editorUuid,
                variableGroups,
                onSave: (value) => {
                  finalRef.current?.replaceContent(value);
                  handleChange(value);
                  hideModal();
                },
              })
            }
          />
        )}
        {variableGroups && (
          <IconButton
            icon={<IconAddSmall />}
            variant="blue-white"
            size="sm"
            dataTestid="field-mapping-open-picker"
            onClick={() => {
              !isExtended || (isExtended && extendedId !== editorUuid)
                ? showFieldMappingSidebar()
                : hideExtendedSidebar();
            }}
          />
        )}
      </Stack>
    );

    // rich (multi-line, formatted) field-mapping rows are PILL_HEIGHT + PILL_ROW_GAP tall with a
    // top pad of one gap, so 3 lines is 3 rows + that pad — not the base editor's 3 × 24px.
    const resolvedMinHeight =
      rest.minHeight ??
      (!rest.singleLine && !rest.plaintext ? pxToRem(FIELD_MAPPING_REST_MIN_HEIGHT_PX) : undefined);

    return (
      <FieldMappingContext.Provider
        value={{
          showFieldMappingSidebar,
          variableGroups: variableGroups ?? [],
          isEditMode,
          hasFormatting: hasFormatting ?? ['text', 'paragraph'].includes(fieldType),
          disableExpandable,
          formLinkType,
        }}
      >
        <StyledEditor
          isActive={isThisOneExtended}
          className={classNames(FIELD_MAPPING_TEXT_EDITOR_CLASS_NAME, className)}
          defaultValue={parsedDefaultValue}
          extensions={[
            FieldMappingNode,
            Focus.configure({ mode: 'deepest' }),
            ...(extensions ?? []),
          ]}
          ref={finalRef}
          actions={Actions}
          onChange={(data) => handleChange(data.value)}
          onFocus={() => {
            const actionsElement = document.querySelector(`#${actionsId}`);
            if (actionsElement && actionsElement.contains(document.activeElement)) {
              return;
            }
            if (
              (isExtended || shouldShowFieldMappingOnFocus) &&
              variableGroups &&
              extendedId !== editorUuid
            ) {
              showFieldMappingSidebar();
            }
          }}
          {...rest}
          minHeight={resolvedMinHeight}
        />
      </FieldMappingContext.Provider>
    );
  },
);

const StyledEditor = styled(TextEditor)(({ singleLine }) => {
  // Every field-mapping editor grows with content and gaps its pill rows: line-height carries the
  // PILL_ROW_GAP so a single pill never outgrows the line (no shift) and wrapped pills don't touch.
  // Single-line grows from one pill (minHeight); multi-line pads its top by the same gap.
  return {
    '.ProseMirror': {
      lineHeight: pxToRem(PILL_HEIGHT_PX + PILL_ROW_GAP_PX),
      ...(singleLine
        ? { minHeight: pxToRem(PILL_HEIGHT_PX) }
        : { paddingTop: pxToRem(PILL_ROW_GAP_PX) }),
    },
    '.ProseMirror-hideselection .has-focus': {
      '.placeholder-container': {
        filter: 'contrast(92%)',
      },
    },
    'ol, ul': {
      margin: 0,
      marginBottom: pxToRem(5),
      paddingInlineStart: pxToRem(25),
    },
    '.placeholder-container': {
      height: pxToRem(PILL_HEIGHT_PX),
    },
    '.node-wrapper': pillRowAlignment,
  };
});

export const NodeStyledFieldMappingTextEditor = styled(FieldMappingTextEditor)({
  cursor: 'pointer',
});

// This styled(FieldMappingTextEditor) variant must be defined in this module, not the
// json editor's: the form ↔ text-editors import cycle means a cross-module styled()
// call can execute before FieldMappingTextEditor initializes (TDZ crash in production
// bundles, where Rollup flattens the cycle into one eval order).
export const JsonHighlightedFieldMappingTextEditor = styled(FieldMappingTextEditor)({
  '.json-token-property': { color: theme.color.elementBlue100 },
  '.json-token-string': { color: theme.color.elementGreen100 },
  '.json-token-number': { color: theme.color.elementOrange100 },
  '.json-token-keyword': { color: theme.color.elementIndigo100 },
});

const locale = {
  en: {
    format: 'Format',
  },
};
